import React, { useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import ProductUtils from "../utils/ProductUtils";
import { useCreateBid } from "@/Apollo/hooks/catalog/mutations/useCreateBid";
import { AuctionState } from "@/Apollo/schema";
import { Zap as ZapIcon } from "@/assets/icons/24/Zap";
import { useAuth } from "@/auth";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { useInitiateBiddingModal } from "@/common/hooks/bidding.hooks";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { CART_OVERVIEW_URL } from "@/common/static/checkout/checkout.static";
import { CHAT_ROUTE } from "@/common/static/routes.static";
import { bidErrorMessage } from "@/common/utils/bid-errors.utils";
import { cn } from "@/common/utils/css.utils";
import { useAskSeller } from "@/components/ProductDetail/hooks/useAskSeller.hooks";
import { useProductIsInCart } from "@/components/ProductDetail/hooks/useProductIsInCart";
import { CommonModal } from "@/components/common/modal/CommonModal";
import { BoostAdModalContent } from "@/features/create-ad-packages/components/BoostAdModalContent";
import { usePdpReservation } from "@/features/pro-buyer-reservations/hooks/usePdpReservation";
import { withReservationPrice } from "@/features/pro-buyer-reservations/utils/reservation-format";
import { useVerifyAccountModalGuard } from "@/features/verify-account/hooks/useVerifyAccountModalGuard";
import useMerchant from "@/hooks/useMerchant";
import addToast from "@/toast/addToast";
import { Button, ButtonProps } from "@/ui/cva/Button";
import useAddProductToCart from "@whoppah/apollo/hooks/useAddProductToCart";
import CartCountGql from "@whoppah/apollo/query/cartCount.graphql";
import useGuestCheckout from "@whoppah/components/checkout2/hooks/useGuestCheckout";
import { Check as CheckIcon } from "@whoppah/icons/24/line/Check";
import { closeModal, showModal } from "@whoppah/ui/Modal2";

const ProductDetailsInfoBidAction = (
  props: Omit<ButtonProps, "appearance">
) => {
  const { t } = useTranslation();

  return (
    <Button appearance="outlined" {...props}>
      {t("pdp:pdp-place-bid-btn")}
    </Button>
  );
};

const ProductDetailsInfoBuyAction = (
  props: Omit<ButtonProps, "rightIcon"> & {
    isInCart: boolean;
    /** WHO-5947: the reserving Pro Buyer reads "Buy now". */
    buyNowLabel?: boolean;
  }
) => {
  const { t } = useTranslation();

  const { isInCart, buyNowLabel = false, ...rest } = props;

  if (buyNowLabel && !isInCart) {
    return <Button {...rest}>{t("pdp:reservation-buy-now", "Buy now")}</Button>;
  }

  return (
    <Button
      rightIcon={
        isInCart ? (
          <CheckIcon className="h-5 w-5 shrink-0 text-white" />
        ) : undefined
      }
      {...rest}
    >
      {isInCart ? t("pdp:pdp-already-in-cart") : t("pdp:pdp-add-to-cart")}
    </Button>
  );
};

type ProductDetailsInfoBidBuyProps = {
  product: ProductBySlugNonNullable;
  className?: string;
};

export const ProductDetailsInfoBidBuy = ({
  product,
  className = undefined,
}: ProductDetailsInfoBidBuyProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { isLoggedIn } = useAuth();
  const { verified } = useMerchant();
  const { guestGuard } = useGuestModalGuard();
  const { guard: verifyGuard } = useVerifyAccountModalGuard();
  const { addProduct: addProductToGuestCart } = useGuestCheckout();
  const { checkIsInCart, isInCart } = useProductIsInCart(product);

  const [isLoading, setIsLoading] = useState(false);

  const [addProductToCart] = useAddProductToCart({
    refetchQueries: [{ query: CartCountGql }],
  });

  const [createBid] = useCreateBid();
  const initiateBiddingModal = useInitiateBiddingModal();

  const allowBid = product.auction?.allow_bid ?? false;
  const buyNowAmount = product.auction?.buy_now_price?.amount;
  const minimumBidAmount = product.auction?.minimum_bid?.amount;
  // The auction state alone, the gateway's rule: only a `BID_ONLY` auction never takes an offer as
  // a purchase, so only there is buy-now no cap on the quick offers (WHO-6211). Not `isBidOnly`
  // below, which also needs an accepted product and picks which buttons the page shows.
  const bidOnlyAuction = product.auction?.state === AuctionState.BidOnly;
  // `unconverted` is the listing's own buy-now, null unless the page shows it in another currency.
  const listingBuyNowAmount =
    product.auction?.buy_now_price?.unconverted?.amount;
  // The buyer bids in the currency the page shows; the gateway converts it to the auction's
  // (WHO-5142, as the apps' PlaceBidViewModel).
  const bidCurrency =
    product.auction?.minimum_bid?.currency ??
    product.auction?.buy_now_price?.currency;
  const isOwner = product.isOwner;
  const vacationMode = !!product.merchant.vacationModeEnabledAt;

  const isSold = ProductUtils.isSold(product);
  const isBidOnly = ProductUtils.isBidOnly(product);

  // WHO-5947: reserved for another buyer locks buying and offers (the
  // gateway refuses them too); the reserving Pro Buyer gets "Buy now".
  const reservation = usePdpReservation(product.id, !!isOwner);
  const reservedForOthers = reservation.mode === "others";
  const reservedForMe = reservation.mode === "mine";
  const { askSeller } = useAskSeller(product);

  const handleAddToCartClick = async (isInCart: boolean) => {
    // if it is already in cart, redirect user to cart page
    if (isInCart) {
      void router.push(CART_OVERVIEW_URL);
      return;
    }

    localStorage.setItem("checkout_source", "pdp_add_to_cart_button");

    if (isLoggedIn) {
      setIsLoading(true);

      try {
        await addProductToCart({
          variables: { product_id: product.id },
        });

        /** Trigger `add_to_cart` event. */
        // WHO-5947 F4 + F11: the holder's "Buy now" behaves like buy now:
        // straight to the cart, tracked at the payable reservation price.
        if (reservation.mode === "mine") {
          analyticsService.trackAddToCart(
            withReservationPrice(product, reservation.reservation.fixedPrice)
          );
          void router.push(CART_OVERVIEW_URL);
          return;
        }

        analyticsService.trackAddToCart(product);

        showModal("pdp-add-to-cart", { product });
      } catch (ex) {
        console.error("ProductDetailsInfoBidBuy", ex);
      } finally {
        setIsLoading(false);
      }

      return;
    }

    void addProductToGuestCart(product.id);
    checkIsInCart(product);

    /** Trigger `add_to_cart` event. */
    analyticsService.trackAddToCart(product);

    showModal("pdp-add-to-cart", { product });
  };

  /**
   * Opens the guest account modal which will allow user to authenticate.
   * After the successful authentication the bid will be created.
   * @param bidAmount - The amount of the bid.
   * @param auctionId
   * @param product
   * @param origin
   * @param loggedIn - Skips the login wall. `isLoggedIn` is from the render
   * the bidding modal was opened in, so a retry after login passes `true`.
   */
  const handleUnauthenticatedBid = async (
    bidAmount: number,
    auctionId: string,
    product: ProductBySlugNonNullable,
    origin?: "left" | "middle" | "right" | "manual",
    loggedIn = isLoggedIn
  ) => {
    /** Not logged-in. */
    if (!loggedIn) {
      const pass = await guestGuard({
        description: t("pdp:pdp-signup-login-wall-initial-desc", {
          productTitle: product.title,
          interpolation: {
            escapeValue: false,
          },
        }),
        referrer: "bidding",
      });

      if (!pass) {
        return;
      }
    }

    /** WHO-6172: back to the offer popup with the amount kept. */
    const reopenBidding = (pendingVerification = false) => {
      if (
        !minimumBidAmount ||
        !bidCurrency ||
        (!buyNowAmount && !bidOnlyAuction)
      ) {
        return;
      }

      void initiateBiddingModal({
        buyNowAmount,
        minimumBidAmount,
        bidOnly: bidOnlyAuction,
        listingBuyNowAmount,
        currency: bidCurrency,
        initialBidAmount: bidAmount,
        pendingVerification,
        onBid: async (value, nextOrigin) => {
          void handleUnauthenticatedBid(
            value,
            auctionId,
            product,
            nextOrigin,
            true
          );
        },
      });
    };

    /** If the user is not verified, we will show verification guard. */
    const verified = await verifyGuard();

    if (!verified || !bidCurrency) {
      reopenBidding(verified === false);
      return;
    }

    try {
      const createBidResponse = await createBid({
        variables: {
          amount: bidAmount,
          auctionId,
          currency: bidCurrency,
        },
        awaitRefetchQueries: true,
      });

      if (origin) {
        /** Trigger `place_bid_success_pdp` event. */
        analyticsService.trackPlaceBidSuccessPdp(origin, product);
      }

      // open confirmation modal after successfully creating a bid.
      const threadId = createBidResponse.data?.createBid?.thread?.id;
      showModal("bidding-confirmation", {
        threadId,
        product,
      });
    } catch (ex) {
      console.error("ProductDetailsInfoBidBuy", ex);

      // Signing up and verifying take the longest, so the minimum is most likely to have moved
      // with the exchange rate here (WHO-6206).
      addToast(bidErrorMessage(ex, t), { appearance: "error" });

      reopenBidding();
    }
  };

  /**
   * Handles the creation of a bid.
   * If the user is not authenticated it will open the account modal which will allow user to authenticate.
   * After creating the bid, user will be redirected to the product's thread.
   * @param bidAmount - The amount of the bid.
   * @param origin - Origin (which button has been pressed) of the bid amount.
   */
  const handleCreateBid = async (
    bidAmount: number,
    origin?: "left" | "middle" | "right" | "manual"
  ) => {
    const auctionId = product.auction?.id;

    if (!bidAmount || !auctionId || !bidCurrency) {
      return;
    }

    if (!isLoggedIn || !verified) {
      void handleUnauthenticatedBid(bidAmount, auctionId, product, origin);
      return;
    }

    try {
      const createBidResponse = await createBid({
        variables: {
          amount: bidAmount,
          auctionId,
          currency: bidCurrency,
        },
        awaitRefetchQueries: true,
      });

      if (origin) {
        /** Trigger `place_bid_success_pdp` event. */
        analyticsService.trackPlaceBidSuccessPdp(origin, product);
      }

      // Redirecting to the product's thread after successfully creating a bid.
      const threadId = createBidResponse.data?.createBid?.thread?.id;
      router.push({
        pathname: threadId ? `${CHAT_ROUTE}/${threadId}` : CHAT_ROUTE,
        query: { return_url: router.asPath },
      });
    } catch (e) {
      console.error("ProductDetailsInfoBidBuy:handleCreateBid", e);

      addToast(bidErrorMessage(e, t), { appearance: "error" });

      // Re-throw so the bidding modal stays open and the user can retry.
      throw e;
    }
  };

  /**
   * Opens the quick bidding modal if all the required data is present.
   */
  const handleBidClick = async () => {
    /** Trigger `place_bid_pdp` event. */
    analyticsService.trackPlaceBidPdp();

    if (
      !product ||
      !minimumBidAmount ||
      !bidCurrency ||
      (!buyNowAmount && !bidOnlyAuction)
    ) {
      return;
    }

    await initiateBiddingModal({
      buyNowAmount,
      minimumBidAmount,
      bidOnly: bidOnlyAuction,
      listingBuyNowAmount,
      currency: bidCurrency,
      onBid: handleCreateBid,
    });
  };

  const handleBoostClick = () => {
    const title = product?.title;
    const boostLabel = t("account-ads:boosts-ad-modal-single-boost-title");

    showModal("common", {
      title: `${boostLabel} - ${title}`,
      closeable: true,
      content: (modal: CommonModal) => (
        <BoostAdModalContent
          product={product}
          onCancel={() => closeModal(modal)}
        />
      ),
    });
  };

  const renderBidBuyButtons = () => {
    if (isOwner) {
      return null;
    }

    if (isBidOnly) {
      return (
        <>
          {/* Desktop */}
          <ProductDetailsInfoBidAction
            // size="large"
            className="pdp-bid-button hidden md:block"
            onClick={handleBidClick}
          />
          {/* Mobile */}
          <ProductDetailsInfoBidAction
            // size="large"
            className="pdp-bid-button md:hidden"
            onClick={handleBidClick}
          />
        </>
      );
    }

    if (reservedForOthers) {
      return (
        <>
          <ProductDetailsInfoBuyAction
            data-testid="pdp.add-to-cart"
            isInCart={false}
            disabled
            className="order-2 md:order-1"
          />
          <Button
            appearance="outlined"
            className="order-1 md:order-2"
            onClick={() => void askSeller()}
          >
            {t("pdp:reservation-ask-question", "Ask a question")}
          </Button>
        </>
      );
    }

    return (
      <>
        {/* Desktop */}
        <ProductDetailsInfoBuyAction
          buyNowLabel={reservedForMe}
          data-testid="pdp.add-to-cart"
          // size="large"
          isInCart={isInCart}
          loading={isLoading}
          className="hidden md:block"
          onClick={() => handleAddToCartClick(isInCart)}
        />
        {/* Mobile */}
        <ProductDetailsInfoBuyAction
          buyNowLabel={reservedForMe}
          data-testid="pdp.add-to-cart"
          // size="large"
          isInCart={isInCart}
          loading={isLoading}
          className="order-2 md:order-1 md:hidden"
          onClick={() => handleAddToCartClick(isInCart)}
        />
        {allowBid && !reservedForMe && (
          <>
            {/* Desktop */}
            <ProductDetailsInfoBidAction
              // size="large"
              className="pdp-bid-button hidden md:block"
              onClick={handleBidClick}
            />
            {/* Mobile */}
            <ProductDetailsInfoBidAction
              // size="large"
              className="pdp-bid-button order-1 md:order-2 md:hidden"
              onClick={handleBidClick}
            />
          </>
        )}
      </>
    );
  };

  const renderBoostAdButton = () => {
    if (!isOwner || product.isBoosted) {
      return null;
    }

    return (
      <Button
        type="button"
        className="pdp-boost-button"
        rightIcon={<ZapIcon className="shrink-0 text-burst-500" />}
        loading={isLoading}
        size="large"
        onClick={handleBoostClick}
      >
        {t("pdp:pdp-boost-your-ad")}
      </Button>
    );
  };

  if (isSold || vacationMode) {
    return null;
  }

  const twoActions =
    !isOwner && ((allowBid && !reservedForMe) || reservedForOthers);
  const bidBuyButtons = renderBidBuyButtons();
  const boostAdButton = renderBoostAdButton();

  if (!bidBuyButtons && !boostAdButton) {
    return null;
  }

  return (
    <div
      className={cn(
        "grid gap-2 md:grid-cols-1 md:gap-2",
        twoActions && "grid-cols-2",
        className
      )}
    >
      {bidBuyButtons}
      {boostAdButton}
    </div>
  );
};
